import React, { useState } from 'react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { toast } from './hooks/use-toast';
import { useLanguage } from './contexts/LanguageContext';

const SimplePartnerForm = ({ onBack, onSuccess }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    experience: '',
    bankAccount: '',
    termsAccepted: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(''); // Visual feedback instead of alerts

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFeedback(`Updated ${field}: ${value}`);
  };

  const handleSubmit = async () => {
    setFeedback('🔥 SUBMIT BUTTON CLICKED! Starting partner registration...');
    
    setTimeout(async () => {
      // Generate unique email to avoid conflicts
      const timestamp = Date.now();
      const defaultEmail = `test.partner.${timestamp}@example.com`;
      
      const data = {
        firstName: formData.firstName || 'Test',
        lastName: formData.lastName || 'Partner', 
        email: formData.email || defaultEmail
      };
      
      if (!data.firstName || !data.lastName || !data.email) {
        setFeedback(`❌ ${t('partner.register.error.validation', 'ERROR: Please fill in Name, Last Name and Email!')}`);
        return;
      }
      
      if (!formData.termsAccepted) {
        setFeedback(`❌ ${t('partner.register.error.terms', 'ERROR: Please accept the terms!')}`);
        return;
      }
      
      setFeedback(prev => prev + '\n✅ VALIDATION SUCCESSFUL! Testing backend connection...');
      
      // Simple URL fallback logic
      const testUrl = window.location.origin.replace(':3000', ':8001') + '/api/partners/register';
      setFeedback(prev => prev + `\n🌐 Using URL: ${testUrl}`);
      
      try {
        const payload = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: formData.phone || t('contact.phone', '+41 76 766 94 30'),
          address: formData.address || t('contact.address', 'Switzerland'),
          experience: formData.experience || 'Partner Registration via visual feedback',
          specializations: ['general_cleaning'],
          hasEquipment: false,
          hasTransport: false,
          availability: {
            monday: { available: true, from: '09:00', to: '17:00' },
            tuesday: { available: false, from: '', to: '' },
            wednesday: { available: false, from: '', to: '' },
            thursday: { available: false, from: '', to: '' },
            friday: { available: false, from: '', to: '' },
            saturday: { available: false, from: '', to: '' }
          },
          bankAccount: formData.bankAccount || 'CH0000000000',
          termsAccepted: true,
          dataProtectionAccepted: true
        };
        
        const response = await fetch(testUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        setFeedback(prev => prev + `\n📡 SERVER RESPONSE: Status ${response.status}`);
        
        if (response.ok) {
          const result = await response.json();
          setFeedback(prev => prev + `\n🎉 ${t('partner.register.success', 'SUCCESS! Partner registered!')} \n\n✅ Name: ${result.firstName} ${result.lastName}\n✅ Email: ${result.email}\n✅ Partner ID: ${result.id}`);
          
          // Show success toast
          toast({
            title: `🎉 ${t('msg.success', 'Success!')}`,
            description: t('partner.register.success', 'Your partner registration was successful!'),
            variant: "default",
          });
          
          setTimeout(() => {
            if (onSuccess) {
              setFeedback(prev => prev + '\n🔄 REDIRECTING to confirmation page...');
              onSuccess(result);
            } else {
              setFeedback(prev => prev + '\n🏠 BACK to home page...');
              setTimeout(() => onBack(), 2000);
            }
          }, 2000);
          
        } else {
          const errorData = await response.json().catch(() => null);
          let errorMessage = '';
          let toastTitle = '';
          let toastDescription = '';
          
          if (response.status === 400 && errorData?.detail?.includes('email already exists')) {
            errorMessage = `❌ ${t('partner.register.error.emailExists', 'ERROR: This email address is already registered!')} \n\n💡 ${t('partner.register.error.validation', 'SOLUTION: Please use a different email address or log in with your existing account.')}`;
            toastTitle = t('partner.register.error.emailExists', 'Email already registered');
            toastDescription = t('partner.register.error.emailExists', 'This email address is already in use. Please use a different email.');
          } else if (response.status === 400) {
            errorMessage = `❌ VALIDATION ERROR: ${errorData?.detail || 'Invalid data'}\n\n💡 ${t('partner.register.error.validation', 'Check your inputs and try again.')}`;
            toastTitle = t('partner.register.error.validation', 'Validation error');
            toastDescription = t('partner.register.error.validation', 'Please check your inputs and try again.');
          } else if (response.status === 500) {
            errorMessage = `❌ SERVER ERROR: An internal error occurred.\n\n💡 ${t('partner.register.error.network', 'Please try again in a few minutes.')}`;
            toastTitle = t('msg.error', 'Server Error');
            toastDescription = t('partner.register.error.network', 'A server error occurred. Please try again later.');
          } else {
            errorMessage = `❌ UNKNOWN ERROR: Status ${response.status}\n\n💡 Please contact support.`;
            toastTitle = t('msg.error', 'Unknown Error');
            toastDescription = t('partner.register.error.network', 'An unknown error occurred. Please contact support.');
          }
          
          setFeedback(prev => prev + `\n${errorMessage}`);
          
          // Show error toast
          toast({
            title: toastTitle,
            description: toastDescription,
            variant: "destructive",
          });
        }
        
      } catch (error) {
        const networkError = `💥 ${t('partner.register.error.network', 'NETWORK ERROR')}: ${error.message}\n\n${t('partner.register.error.network', 'Try again in a few seconds.')}`;
        setFeedback(prev => prev + `\n${networkError}`);
        
        // Show network error toast
        toast({
          title: t('partner.register.error.network', 'Network Error'),
          description: t('partner.register.error.network', 'Connection to server failed. Please check your internet connection.'),
          variant: "destructive",
        });
      }
    }, 500);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-bold text-gray-900">
                {t('partner.register.title', 'Partner Registrierung')}
              </CardTitle>
              <p className="text-gray-600 mt-2">
                {t('partner.register.subtitle', 'Plotësoni formularin për t\'u bërë partner i Brillanz & Glanz')}
              </p>
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 text-sm">
                  💡 <strong>Tip:</strong> Use a unique email address for registration. 
                  If you are already registered, use the login.
                </p>
              </div>
            </CardHeader>
            
            <CardContent className="p-8">
              
              {/* FEEDBACK AREA - Visible feedback */}
              {feedback && (
                <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
                  <h4 className="font-bold text-blue-800 mb-2">📢 Status:</h4>
                  <pre className="text-blue-700 whitespace-pre-wrap">{feedback}</pre>
                </div>
              )}
              
              <div className="space-y-6">
                
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">{t('partner.register.firstName', 'Emri')} *</Label>
                    <Input
                      id="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder={t('partner.register.firstName', 'Your first name')}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="lastName">{t('partner.register.lastName', 'Mbiemri')} *</Label>
                    <Input
                      id="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder={t('partner.register.lastName', 'Your last name')}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">{t('partner.register.email', 'Adresa elektronike')} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="example@email.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone">{t('partner.register.phone', 'Telefoni')}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder={t('contact.phone', '+41 76 766 94 30')}
                  />
                </div>

                <div>
                  <Label htmlFor="address">{t('partner.register.address', 'Adresa')}</Label>
                  <Input
                    id="address"
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder={t('contact.address', 'Your address')}
                  />
                </div>

                <div>
                  <Label htmlFor="experience">{t('partner.register.experience', 'Përvojë')}</Label>
                  <Textarea
                    id="experience"
                    value={formData.experience}
                    onChange={(e) => handleInputChange('experience', e.target.value)}
                    placeholder={t('partner.register.experience', 'Describe your experience...')}
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="bankAccount">{t('partner.register.bankAccount', 'Llogari Bankare')}</Label>
                  <Input
                    id="bankAccount"
                    type="text"
                    value={formData.bankAccount}
                    onChange={(e) => handleInputChange('bankAccount', e.target.value)}
                    placeholder="CH0000000000000000000"
                  />
                </div>

                {/* Terms */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={formData.termsAccepted}
                    onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="terms" className="text-sm">
                    {t('partner.register.terms', 'Pranoj kushtet dhe mbrojtjen e të dhënave')} *
                  </Label>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    id="submit-btn"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      backgroundColor: '#059669',
                      color: 'white',
                      padding: '12px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    {t('partner.register.submit', 'DËRGO APLIKIMIN')}
                  </button>
                </div>

                {/* Back Button */}
                <div>
                  <Button
                    onClick={onBack}
                    variant="outline"
                    className="w-full"
                  >
                    {t('partner.register.back', 'Kthehu')}
                  </Button>
                </div>

              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SimplePartnerForm;